import React, { useState, useEffect } from 'react';
import { AlertCircle, Loader2, CheckCircle } from 'lucide-react';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { apiEndpoints } from '../../utils/api';
import { formatCents } from '../../utils/credits';

export const AutoTopupSettings: React.FC = () => {
  const api = useAuthenticatedApi();
  const [settings, setSettings] = useState({
    auto_topup_enabled: false,
    auto_topup_amount: 50,
    auto_topup_threshold: 10,
    has_payment_method: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (api) {
      fetchSettings();
    }
  }, [api]);

  const fetchSettings = async () => {
    try {
      const response = await api.get(apiEndpoints.billing.autoTopupSettings);
      setSettings(response);
    } catch (err) {
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await api.put(apiEndpoints.billing.autoTopupSettings, {
        auto_topup_enabled: settings.auto_topup_enabled,
        auto_topup_amount: settings.auto_topup_amount,
        auto_topup_threshold: settings.auto_topup_threshold
      });
      setSuccess('Settings saved successfully');
      // Hide success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleAmountChange = (field: 'auto_topup_amount' | 'auto_topup_threshold') => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = parseFloat(event.target.value) || 0;
    setSettings({...settings, [field]: value});
  };

  if (loading) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="h-8 w-8 text-brand-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-2">
          Automatic Top-up Settings
        </h3>
        <p className="text-sm text-neutral-600 dark:text-neutral-400">
          Automatically purchase credits when your balance runs low
        </p>
      </div>
      
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
            <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
          </div>
        </div>
      )}
      
      {success && (
        <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
            <p className="text-sm text-green-800 dark:text-green-200">{success}</p>
          </div>
        </div>
      )}
      
      {!settings.has_payment_method && (
        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
            <p className="text-sm text-yellow-800 dark:text-yellow-200">
              You need to make a purchase first to enable automatic top-ups.
            </p>
          </div>
        </div>
      )}

      <div>
        <label className="flex items-start gap-3 cursor-pointer group">
          <input
            type="checkbox"
            checked={settings.auto_topup_enabled}
            onChange={(e) => setSettings({...settings, auto_topup_enabled: e.target.checked})}
            disabled={!settings.has_payment_method}
            className="mt-1 w-4 h-4 text-brand-primary bg-white dark:bg-neutral-900 border-neutral-300 dark:border-neutral-600 rounded focus:ring-brand-primary focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <div className="flex-1">
            <span className="text-sm font-medium text-neutral-900 dark:text-white group-hover:text-neutral-800 dark:group-hover:text-neutral-100">
              Enable automatic top-up
            </span>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-0.5">
              Automatically purchase credits when your balance is low
            </p>
          </div>
        </label>
      </div>

      <div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-start">
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Top-up amount
            </label>
            <div className="relative w-full">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-500 dark:text-neutral-400">
                $
              </span>
              <input
                type="number"
                value={settings.auto_topup_amount}
                onChange={handleAmountChange('auto_topup_amount')}
                disabled={!settings.auto_topup_enabled}
                min="10"
                max="500"
                step="5"
                className="w-full h-12 pl-8 pr-4 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
            <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
              Amount to add when balance is low (min: $10, max: $500)
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Credits per top-up
            </label>
            <div className="relative w-full">
              <input
                type="text"
                readOnly
                value={formatCents(settings.auto_topup_amount)}
                className="w-full h-12 pl-4 pr-4 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:outline-none"
                aria-readonly
              />
            </div>
          </div>
        </div>
      </div>

      <div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-start">
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Trigger threshold
            </label>
            <div className="relative w-full">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-500 dark:text-neutral-400">
                $
              </span>
              <input
                type="number"
                value={settings.auto_topup_threshold}
                onChange={handleAmountChange('auto_topup_threshold')}
                disabled={!settings.auto_topup_enabled}
                min="5"
                max="100"
                step="5"
                className="w-full h-12 pl-8 pr-4 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
            <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
              Purchase credits when balance falls below this amount (min: $5, max: $100)
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Threshold in credits
            </label>
            <div className="relative w-full">
              <input
                type="text"
                readOnly
                value={formatCents(settings.auto_topup_threshold)}
                className="w-full h-12 pl-4 pr-4 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:outline-none"
                aria-readonly
              />
            </div>
          </div>
        </div>
      </div>

      <button
        onClick={handleSave}
        disabled={saving || !settings.has_payment_method}
        className="px-4 py-2 bg-brand-primary text-black font-medium rounded-lg hover:bg-brand-primaryDark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
      >
        {saving ? (
          <>
            <Loader2 size={16} className="animate-spin" />
            Saving...
          </>
        ) : (
          'Save Settings'
        )}
      </button>
    </div>
  );
}; 